import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';
import { clientService, sessionService, exportService } from '../../services';
import { getFeedbackRating, calculateAverageRating } from '../../utils/ratingUtils';
import { getActivityIcon, getActivityTypeColor } from '../../utils/activityIcons';

/**
 * Group Report Page for L&D
 * Multi-Day Training Engagement, Challenges & Action-Plan Insights
 * Aggregated across all participants
 */

// Helper function to calculate subsession duration from activities
const calculateSubsessionDuration = (subSession) => {
  if (subSession.activities && subSession.activities.length > 0) {
    const totalSeconds = subSession.activities.reduce((sum, activity) => {
      const timeLimit = activity.timeLimit || activity.duration || activity.config?.timeLimit || 0;
      return sum + timeLimit;
    }, 0);
    const minutes = Math.ceil(totalSeconds / 60);
    return minutes > 0 ? minutes : subSession.activities.length;
  }
  return null;
};

const GroupReportPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentSession, setCurrentSession] = useState(null);
  const [sessionParticipants, setSessionParticipants] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch session details
        const sessionResponse = await sessionService.getSession(sessionId);
        if (sessionResponse.success) {
          setCurrentSession(sessionResponse.data);
        }

        // Fetch participants
        const participantsResponse = await sessionService.getParticipants(sessionId);
        if (participantsResponse.success) {
          setSessionParticipants(participantsResponse.data || []);
        }

        // Fetch analytics
        const analyticsResponse = await sessionService.getAnalytics(sessionId);
        if (analyticsResponse.success) {
          setAnalytics(analyticsResponse.data);
        }
      } catch (error) {
        console.error('Error fetching report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-500">Loading report data...</p>
        </div>
      </div>
    );
  }

  if (!currentSession) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-500">Session not found</p>
          <Button variant="primary" onClick={() => navigate(-1)} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const totalParticipants = sessionParticipants.length || analytics?.totalParticipants || 0;
  const activeParticipants = sessionParticipants.filter(p => p.status === 'online' || p.status === 'active').length || analytics?.activeParticipants || 0;

  // Get organization and trainer info from session
  const organization = currentSession.organization || { name: currentSession.organizationName || 'Organization' };
  const trainer = currentSession.trainer || { fullName: currentSession.trainerName };

  // Program info
  const programInfo = {
    title: currentSession.name,
    mode: currentSession.sessionType === 'public' ? 'Virtual' : 'In-Person',
    facilitator: currentSession.trainerName || trainer.fullName,
    hostedBy: organization.name,
    totalParticipants,
    activeParticipants,
  };

  // Calculate aggregated stats from real data
  // Calculate engagement based on actual activity participation
  const allSessionActivities = currentSession.subSessions?.flatMap(sub => sub.activities || []) || currentSession.activities || [];
  const totalActivitiesCount = allSessionActivities.length || 1;
  const allActivityResponses = currentSession.activityResponses || analytics?.activityResponses || [];

  // Calculate engagement: (unique participant-activity responses / (participants * activities)) * 100
  const uniqueResponses = new Set(allActivityResponses.map(r => `${r.participantId}_${r.activityId}`)).size;
  const maxPossibleResponses = (totalParticipants || 1) * totalActivitiesCount;
  const calculatedEngagement = maxPossibleResponses > 0 ? Math.round((uniqueResponses / maxPossibleResponses) * 100) : 0;

  // Use participant engagementScore if available, otherwise use calculated engagement
  const participantEngagement = sessionParticipants.reduce((sum, p) => sum + (p.engagementScore || 0), 0);
  const avgEngagement = participantEngagement > 0
    ? Math.round(participantEngagement / (sessionParticipants.length || 1))
    : calculatedEngagement || analytics?.engagementRate || currentSession.engagementRate || 0;

  const totalActivitiesCompleted = sessionParticipants.reduce((sum, p) => sum + (p.activitiesCompleted || 0), 0);
  const totalQuestionsAsked = sessionParticipants.reduce((sum, p) => sum + (p.questionsAsked || 0), 0);

  // Calculate real activity counts from session data
  const allActivities = currentSession.subSessions?.flatMap(sub => sub.activities || []) || currentSession.activities || [];
  const activityResponses = currentSession.activityResponses || analytics?.activityResponses || [];

  // Count real poll/quiz responses
  const pollQuizActivities = allActivities.filter(a => ['poll', 'quiz', 'mcq'].includes(a.type?.toLowerCase()));
  const pollResponses = activityResponses.filter(r => ['poll', 'quiz', 'mcq'].includes(r.type?.toLowerCase())).length
    || analytics?.pollResponses || 0;

  // Count real workbook/reflection submissions
  const workbookReflections = activityResponses.filter(r => ['workbook', 'reflection', 'openquestion', 'open_question'].includes(r.type?.toLowerCase())).length
    || analytics?.workbookReflections || 0;

  // Count real challenges captured
  const totalChallenges = activityResponses.filter(r => r.type?.toLowerCase() === 'challenges').length
    || analytics?.totalChallenges || 0;

  // Count real action plans created
  const totalActionPlans = activityResponses.filter(r => r.type?.toLowerCase() === 'actionplans' || r.type?.toLowerCase() === 'action_plans').length
    || analytics?.totalActionPlans || 0;

  // Calculate average rating from feedback responses (handles emoji ratings)
  const feedbackResponses = activityResponses.filter(r => r.type?.toLowerCase() === 'feedback');
  const avgRating = feedbackResponses.length > 0
    ? calculateAverageRating(feedbackResponses)
    : (currentSession.avgRating ? getFeedbackRating(currentSession.avgRating) : analytics?.avgRating || 0);

  // Overall engagement snapshot - using real data
  const overallSnapshot = {
    engagementScore: avgEngagement,
    pollResponses,
    workbookReflections,
    totalChallenges,
    totalActionPlans,
    avgRating: avgRating ? avgRating.toFixed(1) : '0',
  };

  // Activity type label helper
  const getActivityLabel = (type) => {
    const labels = {
      quiz: 'Quiz', poll: 'Poll', wordCloud: 'Word Cloud', challenges: 'Challenges',
      actionPlans: 'Action Plans', openQuestion: 'Open Question', feedback: 'Feedback',
    };
    return labels[type] || type;
  };

  // Session-wise engagement data - using real data from subsessions
  const sessionEngagement = currentSession.subSessions?.map((subSession, idx) => {
    // Get responses for this subsession's activities
    const subSessionActivityIds = (subSession.activities || []).map(a => a._id || a.id);
    const subSessionResponses = activityResponses.filter(r =>
      subSessionActivityIds.includes(r.activityId) || r.subSessionId === (subSession._id || subSession.id)
    );

    // Calculate real poll participation for this subsession
    const subSessionPollResponses = subSessionResponses.filter(r => ['poll', 'quiz', 'mcq'].includes(r.type?.toLowerCase()));
    const uniquePollParticipants = [...new Set(subSessionPollResponses.map(r => r.participantId))].length;
    const pollParticipation = uniquePollParticipants || 0;

    // Get real Q&A count for this subsession
    const qaResponses = subSessionResponses.filter(r => r.type?.toLowerCase() === 'qa' || r.type?.toLowerCase() === 'question');
    const qaCount = qaResponses.length || 0;

    // Get real reflection count for this subsession
    const reflectionResponses = subSessionResponses.filter(r =>
      ['workbook', 'reflection', 'openquestion', 'open_question'].includes(r.type?.toLowerCase())
    );
    const reflectionCount = reflectionResponses.length || 0;

    // Calculate real engagement score for this subsession
    const subSessionEngagement = subSession.engagementScore ||
      (subSessionResponses.length > 0 ? Math.round((subSessionResponses.length / (totalParticipants || 1)) * 100) : 0);

    // Calculate real rating for this subsession (handles emoji ratings)
    const subSessionFeedback = subSessionResponses.filter(r => r.type?.toLowerCase() === 'feedback');
    const subSessionRating = subSessionFeedback.length > 0
      ? calculateAverageRating(subSessionFeedback)
      : (subSession.rating ? getFeedbackRating(subSession.rating) : 0);

    // Get ALL activities with their response data for this subsession
    const activitiesWithData = (subSession.activities || []).map(activity => {
      const activityResponses = subSessionResponses.filter(r =>
        r.activityId === (activity._id || activity.id) || r.type?.toLowerCase() === activity.type?.toLowerCase()
      );
      const actId = (activity._id || activity.id)?.toString();
      const responseCount = (currentSession?.activityResponseCounts && actId)
        ? (currentSession.activityResponseCounts[actId] || 0)
        : (activity.responses?.length || activityResponses.length || 0);
      const participationRate = totalParticipants > 0 ? Math.round((responseCount / totalParticipants) * 100) : 0;

      return {
        id: activity._id || activity.id,
        type: activity.type,
        title: activity.title || activity.name || getActivityLabel(activity.type),
        question: activity.question || activity.description || '',
        responseCount,
        participationRate,
        status: activity.status || 'pending',
        responses: activity.responses || activityResponses,
      };
    });

    // Get real challenges from this subsession
    const challengeResponses = subSessionResponses.filter(r => r.type?.toLowerCase() === 'challenges');
    const realChallenges = challengeResponses.length > 0 ? [{
      topic: subSession.name,
      items: challengeResponses.slice(0, 5).map(r => ({
        text: r.challenge || r.text || r.response || 'Challenge submitted',
        count: r.votes || 1,
      })),
    }] : [];

    // Get real action plans from this subsession
    const actionPlanResponses = subSessionResponses.filter(r =>
      r.type?.toLowerCase() === 'actionplans' || r.type?.toLowerCase() === 'action_plans'
    );
    const realActionPlans = actionPlanResponses.length > 0 ? [{
      topic: subSession.name,
      items: actionPlanResponses.slice(0, 5).map(r => ({
        text: r.plan || r.action || r.text || r.response || 'Action plan submitted',
        count: r.votes || 1,
      })),
    }] : [];

    // Calculate launched (completed) activities for this subsession
    const ssActivities = subSession.activities || [];
    const ssLaunchedActivities = ssActivities.filter(a => a.status === 'completed' || a.status === 'launched').length;
    const ssTotalActivities = ssActivities.length;

    return {
      subSessionNumber: idx + 1,
      topic: subSession.name,
      duration: (subSession.startTime && subSession.endTime)
        ? `${subSession.duration || Math.round((new Date(subSession.endTime) - new Date(subSession.startTime)) / 60000)} mins`
        : null,
      highlights: {
        launchedActivities: `${ssLaunchedActivities}/${ssTotalActivities}`,
        engagementScore: `${subSessionEngagement}%`,
        feedbackRating: subSessionRating > 0 ? `${subSessionRating.toFixed(1)}/5` : '-/5',
        joinedParticipants: `${totalParticipants}`,
      },
      activities: activitiesWithData,
      challenges: realChallenges.length > 0 ? realChallenges : [{
        topic: subSession.name,
        items: [{ text: 'No challenges reported yet', count: 0 }],
      }],
      actionPlans: realActionPlans.length > 0 ? realActionPlans : [{
        topic: subSession.name,
        items: [{ text: 'No action plans created yet', count: 0 }],
      }],
    };
  }) || [{
    subSessionNumber: 1,
    topic: 'No Sessions Available',
    duration: null,
    highlights: {
      launchedActivities: '0/0',
      engagementScore: '0%',
      feedbackRating: '-/5',
      joinedParticipants: '0',
    },
    activities: [],
    challenges: [{
      topic: 'No Data',
      items: [{ text: 'No challenges reported yet', count: 0 }],
    }],
    actionPlans: [{
      topic: 'No Data',
      items: [{ text: 'No action plans created yet', count: 0 }],
    }],
  }];

  // Future training needs - dynamically generated from session data
  // Extract unique topics from challenges and action plans
  const challengeTopics = sessionEngagement.flatMap(se =>
    se.challenges.flatMap(c => c.items.filter(i => i.text && i.text !== 'No challenges submitted yet').map(i => i.text))
  );
  const actionPlanTopics = sessionEngagement.flatMap(se =>
    se.actionPlans.flatMap(ap => ap.items.filter(i => i.text && i.text !== 'No action plans submitted yet').map(i => i.text))
  );

  // Generate training needs from actual challenges/action plans or show empty state
  const futureTrainingNeeds = challengeTopics.length > 0 || actionPlanTopics.length > 0
    ? [...new Set([...challengeTopics, ...actionPlanTopics])].slice(0, 4)
    : [];

  // L&D actionable insights - dynamically generated based on actual data
  const ldInsights = [];
  if (overallSnapshot.totalChallenges > 0) {
    ldInsights.push(`${overallSnapshot.totalChallenges} specific challenges captured - future training can be more targeted.`);
  }
  if (overallSnapshot.totalActionPlans > 0) {
    ldInsights.push(`${overallSnapshot.totalActionPlans} action plans created - high intent to apply learnings. Consider follow-up nudges.`);
  }
  if (overallSnapshot.engagementScore > 70) {
    ldInsights.push(`Strong engagement at ${overallSnapshot.engagementScore}% indicates effective content delivery and pacing.`);
  }
  if (parseFloat(overallSnapshot.avgRating) >= 4) {
    ldInsights.push(`Excellent session rating of ${overallSnapshot.avgRating}/5 - participants found the training valuable.`);
  }
  if (ldInsights.length === 0) {
    ldInsights.push('Complete more activities to generate actionable insights from participant data.');
  }

  // Generate PDF Report using backend API (new modern template)
  const generatePDF = async () => {
    setIsGeneratingPDF(true);

    try {
      await exportService.exportSessionAnalyticsReportPDF(sessionId, currentSession.code);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-base sm:text-xl font-semibold text-neutral-900">Group Report for L&D</h1>
              <p className="text-xs sm:text-sm text-neutral-500">Aggregated insights across all participants</p>
            </div>
          </div>
          <Button
            variant="primary"
            onClick={generatePDF}
            disabled={isGeneratingPDF}
            className="flex items-center gap-2"
          >
            {isGeneratingPDF ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Generating...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Export Report</span>
              </>
            )}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8 pb-20 sm:pb-4">
        {/* Activity Overview Header */}
        <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-6">Activity Overview</h1>

        {/* Program Info Card */}
        <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-neutral-900">{programInfo.title}</h2>
              <p className="text-neutral-500 text-xs sm:text-sm mt-1 italic">
                (For internal L&D consumption – aggregated across all participants)
              </p>
            </div>
            <div className="flex flex-wrap gap-4 text-xs sm:text-sm">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-100 rounded-full">
                <span className="text-neutral-500">Mode:</span>
                <span className="font-medium text-neutral-900">{programInfo.mode}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-100 rounded-full">
                <span className="text-neutral-500">Facilitator:</span>
                <span className="font-medium text-neutral-900">{programInfo.facilitator}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-100 rounded-full">
                <span className="text-neutral-500">Hosted By:</span>
                <span className="font-medium text-neutral-900">{programInfo.hostedBy}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-neutral-100">
            <div className="text-center">
              <p className="text-xl sm:text-3xl font-bold text-neutral-900">{programInfo.totalParticipants}</p>
              <p className="text-xs sm:text-sm text-neutral-500">Total Expected Participants</p>
            </div>
            <div className="text-center">
              <p className="text-xl sm:text-3xl font-bold text-green-600">{programInfo.activeParticipants}</p>
              <p className="text-xs sm:text-sm text-neutral-500">Active Participants</p>
            </div>
            <div className="text-center">
              <p className="text-xl sm:text-3xl font-bold text-primary-600">{overallSnapshot.engagementScore}%</p>
              <p className="text-xs sm:text-sm text-neutral-500">Engagement Score</p>
            </div>
            <div className="text-center">
              <p className="text-xl sm:text-3xl font-bold text-yellow-600">{overallSnapshot.avgRating}/5</p>
              <p className="text-xs sm:text-sm text-neutral-500">Avg. Rating</p>
            </div>
          </div>
        </div>

        {/* Overall Engagement Snapshot */}
        <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 mb-6">
          <h3 className="text-sm sm:text-lg font-semibold text-neutral-900 mb-4">1. Overall Engagement Snapshot</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-neutral-50 rounded-lg p-4 text-center">
              <p className="text-lg sm:text-2xl font-bold text-green-600">{overallSnapshot.engagementScore}%</p>
              <p className="text-xs text-neutral-500 mt-1">Engagement Score</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4 text-center">
              <p className="text-lg sm:text-2xl font-bold text-blue-600">{overallSnapshot.pollResponses}</p>
              <p className="text-xs text-neutral-500 mt-1">Poll Responses</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4 text-center">
              <p className="text-lg sm:text-2xl font-bold text-purple-600">{overallSnapshot.workbookReflections}</p>
              <p className="text-xs text-neutral-500 mt-1">Reflections</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4 text-center">
              <p className="text-lg sm:text-2xl font-bold text-red-600">{overallSnapshot.totalChallenges}</p>
              <p className="text-xs text-neutral-500 mt-1">Challenges Captured</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4 text-center">
              <p className="text-lg sm:text-2xl font-bold text-primary-600">{overallSnapshot.totalActionPlans}</p>
              <p className="text-xs text-neutral-500 mt-1">Action Plans</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4 text-center">
              <p className="text-lg sm:text-2xl font-bold text-yellow-600">{overallSnapshot.avgRating}</p>
              <p className="text-xs text-neutral-500 mt-1">Avg. Rating /5</p>
            </div>
          </div>
        </div>

        {/* Session-wise Engagement */}
        <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 mb-6">
          <h3 className="text-sm sm:text-lg font-semibold text-neutral-900 mb-6">2. Session-Wise Engagement Summary</h3>

          <div className="space-y-8">
            {sessionEngagement.map((session, idx) => (
              <motion.div
                key={session.subSessionNumber}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={idx > 0 ? 'pt-8 border-t border-neutral-200' : ''}
              >
                {/* Sub-session Header */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs sm:text-sm font-bold rounded-lg">
                    Sub-session {session.subSessionNumber}
                  </span>
                  <h4 className="font-semibold text-neutral-900">{session.topic}</h4>
                  {session.duration && (
                    <span className="text-xs sm:text-sm text-neutral-500">({session.duration})</span>
                  )}
                </div>

                {/* Engagement Highlights */}
                <div className="bg-neutral-50 rounded-lg p-4 mb-4">
                  <p className="text-xs sm:text-sm font-semibold text-neutral-700 mb-3">Engagement Highlights</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="text-center">
                      <p className="text-sm sm:text-lg font-bold text-blue-600">{session.highlights.launchedActivities}</p>
                      <p className="text-xs text-neutral-500">Launched Activities</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm sm:text-lg font-bold text-green-600">{session.highlights.engagementScore}</p>
                      <p className="text-xs text-neutral-500">Engagement Score</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm sm:text-lg font-bold text-yellow-600">{session.highlights.feedbackRating}</p>
                      <p className="text-xs text-neutral-500">Feedback Rating</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm sm:text-lg font-bold text-purple-600">{session.highlights.joinedParticipants}</p>
                      <p className="text-xs text-neutral-500">Participants</p>
                    </div>
                  </div>
                </div>

                {/* All Activities */}
                {session.activities && session.activities.length > 0 && (
                  <div className="border border-blue-200 rounded-lg mb-4">
                    <div className="bg-blue-50 px-4 py-3 border-b border-blue-200 rounded-t-lg">
                      <p className="font-semibold text-blue-800">Activity Details ({session.activities.length} activities)</p>
                    </div>
                    <div className="p-4">
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[800px]">
                          <thead>
                            <tr className="border-b border-neutral-200">
                              <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-500 uppercase">Activity</th>
                              <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-500 uppercase">Type</th>
                              <th className="text-center py-2 px-3 text-xs font-semibold text-neutral-500 uppercase">Responses</th>
                              <th className="text-center py-2 px-3 text-xs font-semibold text-neutral-500 uppercase">Participation</th>
                            </tr>
                          </thead>
                          <tbody>
                            {session.activities.map((activity, aIdx) => (
                              <tr key={activity.id || aIdx} className="border-b border-neutral-50 hover:bg-neutral-50">
                                <td className="py-2 px-3">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${getActivityTypeColor(activity.type)}`}>
                                      {getActivityIcon(activity.type, 'sm')}
                                    </div>
                                    <span className="text-xs sm:text-sm font-medium text-neutral-900">{activity.title}</span>
                                  </div>
                                </td>
                                <td className="py-2 px-3">
                                  <span className="text-xs px-2 py-1 bg-neutral-100 rounded-full text-neutral-600">
                                    {getActivityLabel(activity.type)}
                                  </span>
                                </td>
                                <td className="py-2 px-3 text-center">
                                  <span className="text-xs sm:text-sm font-semibold text-neutral-900">{activity.responseCount}</span>
                                  <span className="text-xs text-neutral-500">/{totalParticipants}</span>
                                </td>
                                <td className="py-2 px-3">
                                  <div className="flex items-center justify-center gap-2">
                                    <div className="w-16 bg-neutral-200 rounded-full h-1.5">
                                      <div
                                        className="h-1.5 rounded-full bg-blue-500"
                                        style={{ width: `${activity.participationRate}%` }}
                                      ></div>
                                    </div>
                                    <span className="text-xs font-medium text-neutral-600">{activity.participationRate}%</span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Challenges & Action Plans */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Challenges */}
                  <div className="border border-red-200 rounded-lg">
                    <div className="bg-red-50 px-4 py-3 border-b border-red-200 rounded-t-lg">
                      <p className="font-semibold text-red-800">Challenges by Topic (Aggregated)</p>
                    </div>
                    <div className="p-4 space-y-4 max-h-64 overflow-y-auto">
                      {session.challenges.map((challenge, cIdx) => (
                        <div key={cIdx}>
                          <p className="text-xs sm:text-sm font-semibold text-primary-600 mb-2">Topic: {challenge.topic}</p>
                          <p className="text-xs text-neutral-500 mb-1.5">Top Challenges Reported:</p>
                          <ol className="space-y-1.5">
                            {challenge.items.map((item, iIdx) => (
                              <li key={iIdx} className="text-xs sm:text-sm text-neutral-700 flex items-start gap-2">
                                <span className="text-neutral-400">{iIdx + 1}.</span>
                                <span>"{item.text}" <span className="text-neutral-500">({item.count} participants)</span></span>
                              </li>
                            ))}
                          </ol>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Plans */}
                  <div className="border border-green-200 rounded-lg">
                    <div className="bg-green-50 px-4 py-3 border-b border-green-200 rounded-t-lg">
                      <p className="font-semibold text-green-800">Action Plans by Topic (Aggregated)</p>
                    </div>
                    <div className="p-4 space-y-4 max-h-64 overflow-y-auto">
                      {session.actionPlans.map((plan, pIdx) => (
                        <div key={pIdx}>
                          <p className="text-xs sm:text-sm font-semibold text-primary-600 mb-2">Topic: {plan.topic}</p>
                          <p className="text-xs text-neutral-500 mb-1.5">Most-Chosen Action Steps:</p>
                          <ol className="space-y-1.5">
                            {plan.items.map((item, iIdx) => (
                              <li key={iIdx} className="text-xs sm:text-sm text-neutral-700 flex items-start gap-2">
                                <span className="text-neutral-400">{iIdx + 1}.</span>
                                <span>{item.text} <span className="text-neutral-500">({item.count} participants)</span></span>
                              </li>
                            ))}
                          </ol>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Future Training Needs & L&D Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Future Training Needs */}
          <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6">
            <h3 className="text-sm sm:text-lg font-semibold text-neutral-900 mb-4">
              3. Future Training Needs
            </h3>
            <p className="text-xs sm:text-sm text-neutral-500 mb-4">Based on Challenges & Action Plans</p>
            {futureTrainingNeeds.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs sm:text-sm font-medium text-neutral-700">Topics from Participant Submissions:</p>
                {futureTrainingNeeds.map((need, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <span className="text-xs sm:text-sm text-neutral-700">{need}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-neutral-500 text-xs sm:text-sm">No challenges or action plans submitted yet.</p>
                <p className="text-neutral-400 text-xs mt-1">Training needs will be generated from participant submissions.</p>
              </div>
            )}
          </div>

          {/* L&D Actionable Insights */}
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-200 p-4 sm:p-6">
            <h3 className="text-sm sm:text-lg font-semibold text-neutral-900 mb-4">
              4. L&D Actionable Insights
            </h3>
            <div className="space-y-3">
              {ldInsights.map((insight, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-white/60 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-xs sm:text-sm text-neutral-700">{insight}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default GroupReportPage;
